/**
 * Helpers for the personalisation "Share design" feature (ALBAU-578).
 *
 * The share copy and the global on/off toggle are configured in theme settings and
 * injected onto `window.personalisation.share` by the buy-button liquid snippet.
 * The per-personalisation toggle lives on the Personalisation metaobject and is
 * surfaced as `personalisation.allowShareLink` (a string "true"/"false") in context.
 */

/**
 * Default copy used when a theme setting is blank or the config is unavailable
 * (e.g. in tests or dev harnesses). Mirrors the defaults in settings_schema.json.
 */
const DEFAULT_SHARE_CONFIG = {
  enabled: false,
  heading: 'Send them a hint',
  body: 'Share your personalised design in just one link.',
  copied: 'Link copied, ready to share',
  imageNote: "Images aren't included; you'll need to send those too.",
  recipientMessage: "Someone's shared a personalised design with you.",
  recipientImageNote: 'Add your images to complete the piece.',
  slotImageNote: 'Add your image to complete this piece.'
};

/**
 * Reads the global share configuration injected onto the window, falling back to
 * sensible defaults for any missing values.
 * @returns {typeof DEFAULT_SHARE_CONFIG} The resolved share config.
 */
export const getShareConfig = () => {
  // @ts-expect-error - window.personalisation is added at runtime by liquid.
  const injected = (typeof window !== 'undefined' && window.personalisation?.share) || {};
  const config = { ...DEFAULT_SHARE_CONFIG };
  // Only override defaults with non-empty injected values so a blank theme
  // setting still renders readable copy.
  for (const key of Object.keys(DEFAULT_SHARE_CONFIG)) {
    if (key === 'enabled') continue;
    const value = injected[`${key}`];
    if (typeof value === 'string' && value.trim() !== '') {
      config[`${key}`] = value;
    }
  }
  config.enabled = injected.enabled === true || injected.enabled === 'true';
  return config;
};

/**
 * Whether share links are enabled for this personalisation.
 * Requires BOTH the global theme toggle and the per-personalisation toggle.
 * @param {Object} options - Options object.
 * @param {Object|null} [options.personalisation] - The personalisation config (from context).
 * @returns {boolean} True when the Share button should be shown.
 */
export const isShareLinkEnabled = ({ personalisation } = {}) => {
  const { enabled } = getShareConfig();
  if (!enabled) return false;
  const allow = personalisation?.allowShareLink;
  return allow === true || allow === 'true';
};

/**
 * Whether the given personalisation slots include an image upload.
 * Image-upload products get extra guidance because uploaded images are not shared.
 * @param {Map<string, { slotType?: string }>|null|undefined} personalisationSlots - The slots map.
 * @returns {boolean} True when at least one IMAGE_UPLOAD slot is present.
 */
export const hasImageUploadSlot = (personalisationSlots) => {
  if (!personalisationSlots || typeof personalisationSlots.values !== 'function') return false;
  for (const slot of personalisationSlots.values()) {
    if (slot?.slotType === 'IMAGE_UPLOAD') return true;
  }
  return false;
};
